import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Панель вложений на карточке сущности — по образцу панелей задач, заметок и встреч.
import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, List, Spin, Typography, Upload } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../../shared/format/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import {
  deleteAttachment,
  getAttachmentsQueryKey,
  getDownloadUrl,
  listAttachments,
  uploadAttachment,
  type AttachmentEntity,
} from '../api/attachments.client.js';

const { Text } = Typography;

/** 1 МБ = 1024², как показывает проводник; иначе размеры расходятся с ожиданием. */
function formatSize(bytes: number): string {
  if (bytes < 1024)
    return i18n.t(
      'crm.features.dynamic.features.attachments.components.entityattachmentspanel.27',
      { p0: bytes },
    );
  if (bytes < 1024 * 1024)
    return i18n.t(
      'crm.features.dynamic.features.attachments.components.entityattachmentspanel.28',
      { p0: Math.round(bytes / 1024) },
    );
  return i18n.t('crm.features.dynamic.features.attachments.components.entityattachmentspanel.29', {
    p0: (bytes / (1024 * 1024)).toFixed(1),
  });
}

interface EntityAttachmentsPanelProps {
  entity: AttachmentEntity;
  entityId: string;
  title?: string;
}

export function EntityAttachmentsPanel({
  entity,
  entityId,
  title = i18n.t('crm.features.features.attachments.components.entityattachmentspanel.файлы'),
}: EntityAttachmentsPanelProps): ReactElement {
  useI18nSubscription();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);

  const queryKey = getAttachmentsQueryKey(workspaceId, entity, entityId);
  const query = useQuery({
    queryKey,
    queryFn: () => listAttachments(entity, entityId),
    enabled: scope !== null,
  });

  // Файл виден и в истории записи — освежаем оба корня.
  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
    ]);
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadAttachment(entity, entityId, file),
    onSuccess: invalidate,
  });

  const deleteMut = useMutation({
    mutationFn: (attachmentId: string) => deleteAttachment(attachmentId),
    onSuccess: invalidate,
  });

  const attachments = query.data ?? [];
  const mutationError = (uploadMut.error ?? deleteMut.error) as Error | null;

  const openFile = async (attachmentId: string): Promise<void> => {
    setActionError(null);
    // Вкладку открываем сразу по клику: после `await` жест пользователя уже
    // «остыл», и блокировщик всплывающих окон молча съедал бы скачивание.
    // Ссылка короткоживущая, поэтому запрашиваем её только в момент клика.
    const tab = window.open('', '_blank');
    if (tab) {
      // Заготовка не должна иметь доступа к нашему window: сюда приедет чужой домен.
      tab.opener = null;
    }
    try {
      const url = await getDownloadUrl(attachmentId);
      if (tab) {
        tab.location.replace(url);
      } else {
        // Вкладку открыть не дали — пробуем напрямую, вдруг блокировщика нет.
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      tab?.close();
      setActionError(
        error instanceof Error
          ? error.message
          : i18n.t(
              'crm.features.features.attachments.components.entityattachmentspanel.не_удалось_открыть_файл',
            ),
      );
    }
  };

  return (
    <div className="px-6 py-5">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--ant-color-text-secondary)]">
          {title}
        </span>
        <Upload
          showUploadList={false}
          beforeUpload={(file) => {
            uploadMut.mutate(file);
            // Загружаем сами: antd не должен слать файл своим запросом.
            return false;
          }}
        >
          <Button size="small" icon={<UploadOutlined />} loading={uploadMut.isPending}>
            {i18n.t(
              'crm.features.features.attachments.components.entityattachmentspanel.загрузить_файл',
            )}
          </Button>
        </Upload>
      </div>

      {mutationError || actionError ? (
        <Alert
          type="error"
          showIcon
          closable
          className="mb-3"
          title={i18n.t(
            'crm.features.features.attachments.components.entityattachmentspanel.ошибка_операции_с_файлом',
          )}
          description={actionError ?? mutationError?.message}
        />
      ) : null}

      {query.isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.attachments.components.entityattachmentspanel.не_удалось_загрузить_файлы',
          )}
          error={query.error}
        />
      ) : query.isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : attachments.length === 0 ? (
        <EmptyState
          text={i18n.t(
            'crm.features.features.attachments.components.entityattachmentspanel.файлов_пока_нет',
          )}
        />
      ) : (
        <List
          size="small"
          dataSource={attachments}
          renderItem={(attachment) => (
            <List.Item
              actions={[
                <Button
                  key="download"
                  size="small"
                  type="text"
                  icon={<DownloadOutlined />}
                  aria-label={i18n.t(
                    'crm.features.dynamic.features.attachments.components.entityattachmentspanel.30',
                    { p0: attachment.originalName },
                  )}
                  onClick={() => void openFile(attachment.id)}
                />,
                <DeleteEntityButton
                  key="delete"
                  iconOnly
                  label={i18n.t(
                    'crm.features.features.attachments.components.entityattachmentspanel.удалить_файл',
                  )}
                  title={i18n.t(
                    'crm.features.features.attachments.components.entityattachmentspanel.удалить_файл_2',
                  )}
                  description={i18n.t(
                    'crm.features.features.attachments.components.entityattachmentspanel.файл_пропадет_из_карточки_запись_об_этом_о',
                  )}
                  loading={deleteMut.isPending && deleteMut.variables === attachment.id}
                  onConfirm={() => deleteMut.mutate(attachment.id)}
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  <span className="text-sm text-[var(--ant-color-text)]">
                    {attachment.originalName}
                  </span>
                }
                description={
                  <Text type="secondary" className="text-xs">
                    {formatSize(attachment.size)} · {formatDateTime(attachment.createdAt)}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
